import { useMemo } from 'react'
import { useDashboardStore } from '@/state/useDashboardStore'
import { useTenant } from '@/tenant/TenantProvider'
import { useSettingsStore } from '@/state/useSettingsStore'
import { DEFAULT_MILESTONES } from './definitions'
import { computeEvolution, currentAndNextGoal, progressToNextTier } from './evolution'
import type { CurrentGoal, TierProgress } from './evolution'
import type { Category, CategoryTiers, MilestoneDefinition } from './types'

export interface EffectiveTiersResult {
  defs: MilestoneDefinition[]
  computedTiers: CategoryTiers
  tiers: CategoryTiers
  
  goalByCategory: Record<Category, CurrentGoal>
  
  tierProgressByCategory: Record<Category, TierProgress>
  hasDebugOverride: boolean
}

export function useEffectiveTiers(): EffectiveTiersResult {
  const tenant = useTenant()
  const snapshot = useDashboardStore((s) => s.snapshot)
  const debugTiers = useSettingsStore((s) => s.debugTiers)

  const defs: MilestoneDefinition[] = useMemo(
    () =>
      (tenant.milestones.defaultsEnabled ? DEFAULT_MILESTONES : []).concat(
        tenant.milestones.customDefinitions ?? [],
      ),
    [tenant],
  )

  const computedTiers = useMemo(() => computeEvolution(snapshot), [snapshot])

  const tiers: CategoryTiers = useMemo(
    () => ({
      growth: debugTiers.growth ?? computedTiers.growth,
      reliability: debugTiers.reliability ?? computedTiers.reliability,
      revenue: debugTiers.revenue ?? computedTiers.revenue,
      speed: debugTiers.speed ?? computedTiers.speed,
    }),
    [computedTiers, debugTiers],
  )

  const goalByCategory = useMemo<Record<Category, CurrentGoal>>(
    () => ({
      growth: currentAndNextGoal(snapshot, 'growth'),
      revenue: currentAndNextGoal(snapshot, 'revenue'),
      reliability: currentAndNextGoal(snapshot, 'reliability'),
      speed: currentAndNextGoal(snapshot, 'speed'),
    }),
    [snapshot],
  )

  const tierProgressByCategory = useMemo<Record<Category, TierProgress>>(
    () => ({
      growth: progressToNextTier(snapshot, 'growth'),
      revenue: progressToNextTier(snapshot, 'revenue'),
      reliability: progressToNextTier(snapshot, 'reliability'),
      speed: progressToNextTier(snapshot, 'speed'),
    }),
    [snapshot],
  )

  return {
    defs,
    computedTiers,
    tiers,
    goalByCategory,
    tierProgressByCategory,
    hasDebugOverride: Object.keys(debugTiers).length > 0,
  }
}
